import { type LegendListRef } from "@legendapp/list/react";
import { type EnvironmentId, type MessageId, type TurnId } from "@cmmd-forge/contracts";
import { memo, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon, Minimize2Icon } from "lucide-react";
import { cn } from "~/lib/utils";
import { observeVisibleAnimation } from "~/lib/visibleAnimation";
import {
  deriveTimelineEntries,
  formatElapsed,
  type GitHubActionRequiredRequest,
  type GitHubToolConfirmationRequest,
} from "../../session-logic";
import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";
import type { TurnDiffSummary } from "../../types";
import type { ExpandedImagePreview } from "./ExpandedImagePreview";
import { useThreadComposerLayout } from "./threadComposerLayoutContext";
import { ThreadTimelineViewport } from "./ThreadTimelineViewport";
import { TimelineEmptyState } from "./TimelineEmptyState";
import { ProposedPlanCard } from "./ProposedPlanCard";
import { formatTimestamp } from "../../timestampFormat";
import { resolveLatestTurnDiffId } from "./latestTurnDiff";
import { deriveUnsettledTurnId } from "./MessagesTimeline.turnFolds";
import {
  deriveMessagesTimelineRowsWithState,
  type MessagesTimelineRow,
  type MessagesTimelineRowsProjection,
  type TimelineLatestTurn,
} from "./MessagesTimeline.logic";
import {
  computeStableMessagesTimelineRows,
  type StableMessagesTimelineRowsState,
} from "./MessagesTimeline.rowTypes";
import {
  TimelineRowActivityCtx,
  TimelineRowCtx,
  type TimelineRowActivityState,
  type TimelineRowSharedState,
} from "./TimelineRowContext";
import {
  LiveWorkEntryTimelineRow,
  WorkGroupSection,
  WorkGroupToggleTimelineRow,
} from "./TimelineLiveRows";
import { ActivityGroupTimelineRow } from "./TimelineActivityGroupRow";
import {
  AssistantMetaTimelineRow,
  AssistantTimelineRow,
  ReasoningTimelineRow,
} from "./TimelineAssistantRows";
import { TimelineUserMessageRow } from "./TimelineUserMessageRow";
import type { RuntimeAdmissionState } from "./useRuntimeAdmission";
import { insertAdmissionLaunchRow } from "./MessagesTimeline.admission";
import { TimelineWorktreeSetupRow } from "./TimelineWorktreeSetupRow";

interface MessagesTimelineProps {
  isWorking: boolean;
  activeTurnInProgress: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
  /** The thread's latest turn (state, start, completion): drives folds and liveness. */
  latestTurn?: TimelineLatestTurn | null;
  runningTurnId?: TurnId | null;
  listRef: React.RefObject<LegendListRef | null>;
  timelineEntries: ReturnType<typeof deriveTimelineEntries>;
  turnDiffSummaryByAssistantMessageId: Map<MessageId, TurnDiffSummary>;
  routeThreadKey: string;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  onRevertUserMessage: (messageId: MessageId) => void;
  onApproveGitHubConfirmation?: (confirmation: GitHubToolConfirmationRequest) => Promise<void>;
  onRetryGitHubActionRequired?: (actionRequired: GitHubActionRequiredRequest) => Promise<void>;
  activeActionRequired?: GitHubActionRequiredRequest | null;
  suppressedGitHubConfirmationId?: string | null;
  declinedGitHubConfirmationId?: string | null;
  isRevertingCheckpoint: boolean;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  activeThreadEnvironmentId: EnvironmentId;
  markdownCwd: string | undefined;
  /** Skills the active provider exposes, so `$name` renders as a chip. */
  providerSkills: ReadonlyArray<ServerProviderSkill>;
  resolvedTheme: "light" | "dark";
  timestampFormat: TimestampFormat;
  workspaceRoot: string | undefined;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  emptyStateProjectName?: string | null;
  runtimeAdmission?: RuntimeAdmissionState | null | undefined;
}

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.tsx at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export const MessagesTimeline = memo(function MessagesTimeline({
  isWorking,
  activeTurnInProgress,
  activeTurnId,
  activeTurnStartedAt,
  latestTurn = null,
  runningTurnId = null,
  listRef,
  timelineEntries,
  turnDiffSummaryByAssistantMessageId,
  routeThreadKey,
  onOpenTurnDiff,
  onRevertUserMessage,
  onApproveGitHubConfirmation,
  onRetryGitHubActionRequired,
  activeActionRequired = null,
  suppressedGitHubConfirmationId = null,
  declinedGitHubConfirmationId = null,
  isRevertingCheckpoint,
  onImageExpand,
  activeThreadEnvironmentId,
  markdownCwd,
  providerSkills,
  resolvedTheme,
  timestampFormat,
  workspaceRoot,
  onIsAtEndChange,
  emptyStateProjectName,
  runtimeAdmission = null,
}: MessagesTimelineProps) {
  const { timelineInset } = useThreadComposerLayout();
  // An in-session interrupt leaves its turn expanded so the user keeps their
  // place; the next turn (or a reload, since this is local state) folds it.
  const [expandedTurnIds, setExpandedTurnIds] = useState<ReadonlySet<TurnId>>(() => new Set());
  const [expandedWorkGroupIds, setExpandedWorkGroupIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [expandedSpawnEntryIds, setExpandedSpawnEntryIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [expandedReasoningMessageIds, setExpandedReasoningMessageIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const previousLatestTurnRef = useRef<TimelineLatestTurn | null>(latestTurn);
  useEffect(() => {
    const previous = previousLatestTurnRef.current;
    previousLatestTurnRef.current = latestTurn;
    if (!latestTurn || previous?.turnId === undefined) {
      return;
    }
    if (latestTurn.turnId === previous.turnId) {
      if (previous.state === "running" && latestTurn.state === "interrupted") {
        setExpandedTurnIds((existing) => {
          const next = new Set(existing);
          next.add(latestTurn.turnId);
          return next;
        });
      }
      return;
    }
    setExpandedTurnIds((existing) => {
      if (!existing.has(previous.turnId)) {
        return existing;
      }
      const next = new Set(existing);
      next.delete(previous.turnId);
      return next;
    });
  }, [latestTurn]);

  const onToggleTurnFold = useCallback((turnId: TurnId) => {
    setExpandedTurnIds((existing) => {
      const next = new Set(existing);
      if (next.has(turnId)) {
        next.delete(turnId);
      } else {
        next.add(turnId);
      }
      return next;
    });
  }, []);
  const onToggleWorkGroup = useCallback((groupId: string) => {
    setExpandedWorkGroupIds((existing) => {
      const next = new Set(existing);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);
  const onToggleWorkEntry = useCallback((anchorKey: string, collapsed: boolean) => {
    setExpandedWorkGroupIds((existing) => {
      const next = new Set(existing);
      if (collapsed) next.delete(anchorKey);
      else next.add(anchorKey);
      return next;
    });
  }, []);
  const onToggleSpawnRow = useCallback((entryId: string, expanded: boolean) => {
    setExpandedSpawnEntryIds((current) => {
      if (current.has(entryId) === expanded) return current;
      const next = new Set(current);
      if (expanded) next.add(entryId);
      else next.delete(entryId);
      return next;
    });
  }, []);
  const onToggleReasoning = useCallback((messageId: string, expanded: boolean) => {
    setExpandedReasoningMessageIds((current) => {
      if (current.has(messageId) === expanded) return current;
      const next = new Set(current);
      if (expanded) next.add(messageId);
      else next.delete(messageId);
      return next;
    });
  }, []);

  const rowsProjectionRef = useRef<MessagesTimelineRowsProjection | null>(null);
  const rawRows = useMemo(() => {
    const projection = deriveMessagesTimelineRowsWithState(
      {
        timelineEntries,
        latestTurn,
        runningTurnId,
        expandedTurnIds,
        expandedWorkGroupIds,
        isWorking,
        activeTurnStartedAt,
        turnDiffSummaries: [...turnDiffSummaryByAssistantMessageId.values()],
        supportsConversationRollback: true,
      },
      rowsProjectionRef.current,
    );
    rowsProjectionRef.current = projection;
    return insertAdmissionLaunchRow(
      projection.rows,
      timelineEntries,
      runtimeAdmission,
      latestTurn?.startedAt,
    );
  }, [
    timelineEntries,
    latestTurn,
    runningTurnId,
    expandedTurnIds,
    expandedWorkGroupIds,
    isWorking,
    activeTurnStartedAt,
    turnDiffSummaryByAssistantMessageId,
    runtimeAdmission,
  ]);
  const rows = useStableRows(rawRows);
  const latestTurnDiffId = resolveLatestTurnDiffId(turnDiffSummaryByAssistantMessageId);
  const unsettledTurnId = deriveUnsettledTurnId(latestTurn, runningTurnId);
  // Nested tool-group scroll positions and entry disclosures share one view
  // state per thread; the key keeps a thread switch from inheriting them.
  const workGroupViewState = useMemo(() => {
    void routeThreadKey;
    return { scrollPositions: new Map(), expandedEntries: new Set<string>() };
  }, [routeThreadKey]);

  const sharedState = useMemo<TimelineRowSharedState>(
    () => ({
      activeTurnInProgress,
      activeTurnId: activeTurnId ?? null,
      latestTurnDiffId,
      isWorking,
      isRevertingCheckpoint,
      timestampFormat,
      routeThreadKey,
      markdownCwd,
      providerSkills,
      resolvedTheme,
      workspaceRoot,
      activeThreadEnvironmentId,
      onRevertUserMessage,
      onImageExpand,
      onOpenTurnDiff,
      onApproveGitHubConfirmation: onApproveGitHubConfirmation ?? (async () => undefined),
      onRetryGitHubActionRequired: onRetryGitHubActionRequired ?? (async () => undefined),
      activeActionRequired,
      suppressedGitHubConfirmationId,
      declinedGitHubConfirmationId,
      expandedTurnIds,
      expandedWorkGroupIds,
      expandedSpawnEntryIds,
      expandedReasoningMessageIds,
      onToggleTurnFold,
      onToggleWorkGroup,
      onToggleWorkEntry,
      onToggleSpawnRow,
      onToggleReasoning,
      workGroupViewState,
    }),
    [
      activeTurnInProgress,
      activeTurnId,
      latestTurnDiffId,
      isWorking,
      isRevertingCheckpoint,
      timestampFormat,
      routeThreadKey,
      markdownCwd,
      providerSkills,
      resolvedTheme,
      workspaceRoot,
      activeThreadEnvironmentId,
      onRevertUserMessage,
      onImageExpand,
      onOpenTurnDiff,
      onApproveGitHubConfirmation,
      onRetryGitHubActionRequired,
      activeActionRequired,
      suppressedGitHubConfirmationId,
      declinedGitHubConfirmationId,
      expandedTurnIds,
      expandedWorkGroupIds,
      expandedSpawnEntryIds,
      expandedReasoningMessageIds,
      onToggleTurnFold,
      onToggleWorkGroup,
      onToggleWorkEntry,
      onToggleSpawnRow,
      onToggleReasoning,
      workGroupViewState,
    ],
  );
  const activityState = useMemo<TimelineRowActivityState>(
    () => ({
      latestTurnId: latestTurn?.turnId ?? null,
      unsettledTurnId,
      isWorking,
    }),
    [latestTurn?.turnId, unsettledTurnId, isWorking],
  );

  const renderItem = useCallback(
    ({ item }: { item: MessagesTimelineRow }) => (
      <div className="mx-auto w-full min-w-0 max-w-3xl overflow-x-hidden" data-timeline-root="true">
        <TimelineRowContent row={item} />
      </div>
    ),
    [],
  );

  if (rows.length === 0 && !isWorking) {
    return <TimelineEmptyState projectName={emptyStateProjectName} timelineInset={timelineInset} />;
  }

  return (
    <TimelineRowCtx.Provider value={sharedState}>
      <TimelineRowActivityCtx.Provider value={activityState}>
        <ThreadTimelineViewport
          key={routeThreadKey}
          rows={rows}
          listRef={listRef}
          routeThreadKey={routeThreadKey}
          timelineInset={timelineInset}
          onIsAtEndChange={onIsAtEndChange}
          renderItem={renderItem}
        />
      </TimelineRowActivityCtx.Provider>
    </TimelineRowCtx.Provider>
  );
});

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.tsx at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function TimelineRowContent({ row }: { row: MessagesTimelineRow }) {
  const isExpandedToolGroup = row.kind === "work" && row.isExpandedToolGroup;
  const isExpandedToolGroupHeader =
    (row.kind === "work-toggle" && row.expanded) || (row.kind === "work-live" && row.expanded);

  return (
    <div
      className={cn(
        isExpandedToolGroup
          ? "pb-1"
          : isExpandedToolGroupHeader
            ? "pb-0"
            : row.kind === "turn-fold" || row.kind === "working"
              ? "pb-1.5"
              : row.kind === "work" ||
                  row.kind === "work-live" ||
                  row.kind === "work-toggle" ||
                  row.kind === "activity-group" ||
                  row.kind === "thinking" ||
                  (row.kind === "message" &&
                    ((row.message.role === "assistant" && !row.showAssistantMeta) ||
                      row.message.role === "reasoning"))
                ? "pb-2"
                : "pb-4",
        row.kind === "message" && row.message.role === "assistant" ? "group/assistant" : null,
      )}
      data-timeline-row-id={row.id}
      data-timeline-row-kind={row.kind}
      data-message-id={
        row.kind === "message" || row.kind === "assistant-meta" ? row.message.id : undefined
      }
      data-message-role={row.kind === "message" ? row.message.role : undefined}
    >
      {row.kind === "work" ? (
        <WorkGroupSection
          anchorKey={row.id}
          groupedEntries={row.groupedEntries}
          isExpandedToolGroup={row.isExpandedToolGroup}
          displayLabel={row.displayLabel}
        />
      ) : null}
      {row.kind === "worktree-setup" ? <TimelineWorktreeSetupRow row={row} /> : null}
      {row.kind === "work-live" ? <LiveWorkEntryTimelineRow row={row} /> : null}
      {row.kind === "activity-group" ? <ActivityGroupTimelineRow row={row} /> : null}
      {row.kind === "work-toggle" ? <WorkGroupToggleTimelineRow row={row} /> : null}
      {row.kind === "turn-fold" ? <TurnFoldTimelineRow row={row} /> : null}
      {row.kind === "context-compaction" ? <ContextCompactionTimelineRow row={row} /> : null}
      {row.kind === "message" && row.message.role === "user" ? (
        <TimelineUserMessageRow row={row} />
      ) : null}
      {row.kind === "message" && row.message.role === "assistant" ? (
        <AssistantTimelineRow row={row} />
      ) : null}
      {row.kind === "message" && row.message.role === "reasoning" ? (
        <ReasoningTimelineRow row={row} />
      ) : null}
      {row.kind === "assistant-meta" ? <AssistantMetaTimelineRow row={row} /> : null}
      {row.kind === "proposed-plan" ? <ProposedPlanTimelineRow row={row} /> : null}
      {row.kind === "working" ? <WorkingTimelineRow row={row} /> : null}
      {row.kind === "thinking" ? <ThinkingTimelineRow /> : null}
    </div>
  );
}

export function TurnFoldTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "turn-fold" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const Icon = row.expanded ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="group/timeline-row relative flex items-center gap-1 border-b border-border/60 pb-2 pe-0.5 pt-1">
      <button
        type="button"
        aria-expanded={row.expanded}
        data-scroll-anchor-ignore
        onClick={() => ctx.onToggleTurnFold(row.turnId)}
        className="flex cursor-pointer select-none items-center gap-1 rounded-md px-1 text-sm leading-relaxed text-muted-foreground tabular-nums transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      >
        <span>{row.label}</span>
        <Icon className="size-3.5" />
      </button>
      <span className="ms-auto shrink-0 pe-1 font-mono text-2xs tabular-nums text-muted-foreground/60">
        {formatTimestamp(row.createdAt, ctx.timestampFormat)}
      </span>
    </div>
  );
}

function ContextCompactionTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "context-compaction" }>;
}) {
  return (
    <div
      role="separator"
      aria-label={row.label}
      className="mx-auto flex w-full max-w-3xl items-center gap-3 py-1 text-muted-foreground text-xs"
    >
      <span className="h-px flex-1 bg-border/70" />
      <span className="flex shrink-0 items-center gap-1.5">
        <Minimize2Icon aria-hidden="true" className="size-3" />
        {row.label}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}

function ProposedPlanTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "proposed-plan" }>;
}) {
  const ctx = use(TimelineRowCtx);
  return (
    <div className="min-w-0 px-1 py-0.5">
      <ProposedPlanCard
        planMarkdown={row.proposedPlan.planMarkdown}
        environmentId={ctx.activeThreadEnvironmentId}
        cwd={ctx.markdownCwd}
        workspaceRoot={ctx.workspaceRoot}
      />
    </div>
  );
}

function WorkingTimelineRow({ row }: { row: Extract<MessagesTimelineRow, { kind: "working" }> }) {
  return (
    <div className="border-b border-border/60 pb-2 pt-1">
      <div
        ref={observeVisibleAnimation}
        className="flex h-6 min-w-0 items-baseline gap-2 px-1 text-sm leading-relaxed text-muted-foreground tabular-nums"
      >
        <span className="relative shrink-0 overflow-hidden whitespace-nowrap">
          {row.createdAt ? (
            <>
              Working for <WorkingTimer createdAt={row.createdAt} />
            </>
          ) : (
            "Working..."
          )}
        </span>
      </div>
    </div>
  );
}

function ThinkingTimelineRow() {
  return (
    <div className="min-h-7">
      <div
        ref={observeVisibleAnimation}
        className="relative min-h-6 w-fit max-w-full min-w-0 overflow-hidden rounded-md text-sm leading-relaxed"
      >
        <span className="flex min-h-6 min-w-0 items-center gap-1.5 px-1 py-0.5 text-secondary-label">
          <span className="min-w-0 flex-1 truncate live-tool-shine">Thinking</span>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-ticking labels
// ---------------------------------------------------------------------------

/** Live elapsed time for the "Working for" label. */
function WorkingTimer({ createdAt }: { createdAt: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const initialText = formatWorkingTimerNow(createdAt);

  useEffect(() => {
    const updateText = () => {
      if (textRef.current) {
        textRef.current.textContent = formatWorkingTimerNow(createdAt);
      }
    };
    updateText();
    const id = setInterval(updateText, 1000);
    return () => clearInterval(id);
  }, [createdAt]);

  return (
    <span ref={textRef} className="tabular-nums">
      {initialText}
    </span>
  );
}

function formatWorkingTimerNow(startIso: string): string {
  const startedAtMs = Date.parse(startIso);
  if (!Number.isFinite(startedAtMs)) return "0s";
  // formatElapsed returns null on clock skew (createdAt in the future);
  // formatDuration would render that as "1ms", so hold at "0s".
  return formatElapsed(startIso, new Date().toISOString()) ?? "0s";
}

/** Returns a structurally-shared copy of `rows`: for each row whose content
 *  hasn't changed since last call, the previous object reference is reused. */
function useStableRows(rows: MessagesTimelineRow[]): MessagesTimelineRow[] {
  const prevState = useRef<StableMessagesTimelineRowsState>({
    byId: new Map<string, MessagesTimelineRow>(),
    result: [],
  });

  return useMemo(() => {
    const nextState = computeStableMessagesTimelineRows(rows, prevState.current);
    prevState.current = nextState;
    return nextState.result;
  }, [rows]);
}
